import { useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { z } from 'zod/v3'
import { ArrowLeft, Save } from 'lucide-react'

import type { CreateInNoteRequest } from '@/lib/api/scenting-lot-api'
import type { LotOption } from '@/components/lots/lot-items-field'
import { StickyFormFooter } from '@/components/common/StickyFormFooter'
import { Button } from '@/components/ui/button'
import { Combobox } from '@/components/ui/combobox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { LotItemsField } from '@/components/lots/lot-items-field'
import { LotSourcesSubTable } from '@/components/lots/lot-sources-subtable'
import {
    scentingLotOption,
    sortingLotOption,
} from '@/components/lots/lot-option-builders'
import {
    useCreateScentingInNote,
    useScentingLots,
} from '@/hooks/use-scenting-lot-query'
import {
    useSortingLots,
    useSortingWithdrawalNote,
    useSortingWithdrawalNotesInfinite,
} from '@/hooks/use-sorting-lot-query'
import { useDebounced } from '@/hooks/use-debounced'
import { useTeaGradesList } from '@/hooks/use-tea-grade-query'
import { ApiError } from '@/lib/api/client'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

const baseItemFields = {
    // Nguồn tầng dòng (bắt buộc ≥1 khi reason=from_process — kiểm ở superRefine).
    sources: z
        .array(
            z.object({
                source_lot_id: z.string().optional(),
                source_quantity: z.coerce.number().optional(),
            }),
        )
        .optional(),
}

const itemSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('new'),
        new_lot_code: z.string().optional(),
        new_lot_grade_id: z
            .string({ required_error: m.form_err_pick_grade() })
            .uuid(m.form_err_pick_grade()),
        quantity: z.coerce
            .number({
                required_error: m.form_err_quantity_required(),
                invalid_type_error: m.form_err_number_invalid(),
            })
            .positive(m.form_err_quantity_positive()),
        ...baseItemFields,
    }),
    z.object({
        kind: z.literal('existing'),
        scenting_lot_id: z
            .string({ required_error: m.form_err_pick_existing_lot() })
            .uuid(m.form_err_pick_existing_lot()),
        quantity: z.coerce
            .number({
                required_error: m.form_err_quantity_required(),
                invalid_type_error: m.form_err_number_invalid(),
            })
            .positive(m.form_err_quantity_positive()),
        ...baseItemFields,
    }),
])

const schema = z
    .object({
        reason: z.enum([
            'adjustment',
            'from_process',
            'return_from_packaging_process',
        ]),
        notes: z.string().max(2000, m.form_err_notes_too_long()).optional(),
        items: z.array(itemSchema).min(1, m.form_err_items_min_one()),
    })
    .superRefine((data, ctx) => {
        // Luồng đi chuỗi: mỗi dòng phải khai ≥1 lô phân loại nguồn; mỗi nguồn có
        // lô + kg xuất >= 0 (cho phép 0, cấm âm/để trống); không khai trùng lô.
        if (data.reason !== 'from_process') return
        data.items.forEach((it, idx) => {
            const sources = it.sources ?? []
            if (sources.length === 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['items', idx, 'sources'],
                    message: m.scl_new_err_source_lot(),
                })
                return
            }
            const seen = new Set<string>()
            sources.forEach((s, sIdx) => {
                if (!s.source_lot_id) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['items', idx, 'sources', sIdx, 'source_lot_id'],
                        message: m.scl_new_err_source_lot(),
                    })
                } else if (seen.has(s.source_lot_id)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['items', idx, 'sources', sIdx, 'source_lot_id'],
                        message: m.scl_new_err_source_dup(),
                    })
                } else {
                    seen.add(s.source_lot_id)
                }
                if (s.source_quantity == null || s.source_quantity < 0) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['items', idx, 'sources', sIdx, 'source_quantity'],
                        message: m.scl_new_err_source_qty(),
                    })
                }
            })
        })
    })

type FormData = z.infer<typeof schema>
type LotOpt = LotOption

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/scenting-warehouse/stock-in-notes/new',
)({
    component: NewPNKPage,
})

function NewPNKPage() {
    const navigate = useNavigate()
    const create = useCreateScentingInNote()

    const handleSubmit = async (value: CreateInNoteRequest) => {
        try {
            const res = await create.mutateAsync(value)
            const noteId = res.data?.id
            if (noteId)
                void navigate({
                    to: '/scenting-warehouse/stock-in-notes/$id',
                    params: { id: noteId },
                })
        } catch (err) {
            toast.error(
                err instanceof ApiError ? err.message : m.sl_new_error_create(),
            )
        }
    }

    return (
        <ScentingLotStockInForm
            isPending={create.isPending}
            onSubmit={(value) => void handleSubmit(value)}
            onCancel={() =>
                navigate({
                    to: '/scenting-warehouse/stock-in-notes',
                    search: { page: 1 },
                })
            }
        />
    )
}

// TẠM ẨN phiếu lấy hàng khỏi UI (tính năng item-level-lot-lineage chưa hoàn thiện).
// Đặt = true để bật lại picker phiếu lấy hàng trong form. Không xoá code/BE.
const WITHDRAWAL_UI_ENABLED = false

export function ScentingLotStockInForm({
    mode = 'create',
    initialValues,
    initialWithdrawalNoteId,
    title,
    submitLabel,
    seedLotOptions,
    seedSourceOptions,
    isPending,
    onSubmit,
    onCancel,
}: {
    mode?: 'create' | 'edit'
    initialValues?: FormData
    /** Phiếu lấy hàng đã chọn khi tạo — dùng để sửa pre-select lại picker. */
    initialWithdrawalNoteId?: string
    title?: string
    submitLabel?: string
    seedLotOptions?: LotOpt[]
    /** Lô phân loại nguồn đã chọn (để sửa hiện đúng dù note đã trừ cạn). */
    seedSourceOptions?: LotOpt[]
    isPending: boolean
    onSubmit: (value: CreateInNoteRequest) => void
    onCancel: () => void
}) {
    const isEdit = mode === 'edit'
    const { data: gradesData } = useTeaGradesList()
    const { data: lotsData } = useScentingLots({ per_page: 200 })

    // Picker lô phân loại nguồn: tìm phía server + chỉ lô còn tồn.
    const [sourceSearch, setSourceSearch] = useState('')
    const debouncedSourceSearch = useDebounced(sourceSearch, 300)
    const { data: sourceLotsData } = useSortingLots({
        per_page: 50,
        only_available: '1',
        search: debouncedSourceSearch || undefined,
    })

    // Gợi ý theo phiếu lấy hàng kho phân loại (tuỳ chọn). Search theo tiêu đề,
    // debounce qua API, cuộn tải thêm. Chọn note → picker lô nguồn chỉ hiện lô
    // trong note; không lưu gì, chỉ trừ số dư note khi tạo (BE lo).
    const [withdrawalNoteId, setWithdrawalNoteId] = useState(
        initialWithdrawalNoteId ?? '',
    )
    const [withdrawalSearch, setWithdrawalSearch] = useState('')
    const debouncedWithdrawalSearch = useDebounced(withdrawalSearch, 300)
    const withdrawalNotesQuery = useSortingWithdrawalNotesInfinite({
        per_page: 20,
        status: 'open',
        search: debouncedWithdrawalSearch || undefined,
    })
    const withdrawalNoteOptions = useMemo(
        () =>
            (withdrawalNotesQuery.data?.pages ?? [])
                .flatMap((p) => p.data ?? [])
                .map((n) => ({
                    value: n.id,
                    label: `${n.notes ?? n.code} · ${n.code}`,
                })),
        [withdrawalNotesQuery.data],
    )
    const { data: pickedNoteData } = useSortingWithdrawalNote(
        withdrawalNoteId || undefined,
    )
    const noteSourceOptions = useMemo<LotOpt[]>(
        () =>
            (pickedNoteData?.data?.items ?? [])
                .filter((it) => it.quantity > 0) // đã rút cạn thì bỏ
                .map((it) => ({
                    value: it.sorting_lot_id,
                    label: `${it.sorting_lot_code ?? it.sorting_lot_id} — ${it.grade_name ?? ''} (${m.scl_new_source_remaining({ qty: formatQty(it.quantity) })})`,
                    available: it.quantity,
                    unit: 'kg',
                })),
        [pickedNoteData],
    )

    const form = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: initialValues ?? {
            reason: 'from_process',
            notes: '',
            items: [
                { kind: 'new' as const, new_lot_grade_id: '', quantity: 0 },
            ],
        },
    })

    const reason = useWatch({ control: form.control, name: 'reason' })

    const gradeOptions = useMemo(
        () =>
            (gradesData?.data ?? []).map((g) => ({
                value: g.id,
                label: g.name,
            })),
        [gradesData],
    )
    const lotOptions = useMemo<LotOpt[]>(() => {
        const map = new Map<string, LotOpt>()
        for (const opt of seedLotOptions ?? []) map.set(opt.value, opt)
        for (const l of lotsData?.data ?? [])
            map.set(l.id, scentingLotOption(l))
        return Array.from(map.values())
    }, [lotsData, seedLotOptions])
    // Chọn note → options cố định theo lô trong note (lọc client). Không chọn →
    // tìm tự do toàn kho phân loại (server search). Luôn merge seed (lô đã chọn
    // khi sửa) để hiện đúng dù lô đã bị trừ cạn khỏi note.
    const usingNote = !!withdrawalNoteId
    const sourceLotOptions = useMemo<LotOpt[]>(() => {
        const base = usingNote
            ? noteSourceOptions
            : (sourceLotsData?.data ?? []).map(sortingLotOption)
        const map = new Map<string, LotOpt>()
        for (const opt of seedSourceOptions ?? []) map.set(opt.value, opt)
        for (const opt of base) map.set(opt.value, opt) // base thắng (số dư mới)
        return Array.from(map.values())
    }, [usingNote, noteSourceOptions, sourceLotsData, seedSourceOptions])

    const submit = form.handleSubmit((data) => {
        const isChain = data.reason === 'from_process'
        const items = data.items.map((it) => {
            const src = isChain
                ? {
                      sources: (it.sources ?? [])
                          .filter((s) => s.source_lot_id)
                          .map((s) => ({
                              source_lot_id: s.source_lot_id as string,
                              source_quantity: Number(s.source_quantity ?? 0),
                          })),
                  }
                : {}
            return it.kind === 'new'
                ? {
                      new_lot_grade_id: it.new_lot_grade_id,
                      new_lot_code: it.new_lot_code || undefined,
                      quantity: Number(it.quantity),
                      ...src,
                  }
                : {
                      scenting_lot_id: it.scenting_lot_id,
                      quantity: Number(it.quantity),
                      ...src,
                  }
        })
        onSubmit({
            reason: data.reason,
            source_withdrawal_note_id:
                isChain && withdrawalNoteId ? withdrawalNoteId : undefined,
            notes: data.notes,
            items,
        })
    })

    return (
        <div className="space-y-4 p-4 pb-24 md:p-6 md:pb-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={onCancel}>
                        <ArrowLeft className="size-4 mr-1" /> {m.sl_lot_back()}
                    </Button>
                    <h1 className="text-xl font-semibold">
                        {title ?? m.scl_new_pnk_title()}
                    </h1>
                </div>
            </div>

            <FormProvider {...form}>
                <form onSubmit={submit} className="space-y-6 max-w-4xl">
                    <div className="grid gap-4 md:grid-cols-2 rounded-md border bg-card p-4 [&>*]:min-w-0">
                        <div>
                            <Label>{m.sl_new_reason_in()}</Label>
                            <select
                                className="mt-1 h-9 w-full rounded-md border bg-background px-3 text-sm disabled:opacity-60"
                                disabled={isEdit}
                                {...form.register('reason')}
                            >
                                <option value="from_process">
                                    {m.scl_new_reason_in_from_pxk()}
                                </option>
                                <option value="return_from_packaging_process">
                                    {m.scl_in_reason_return_from_packaging_process()}
                                </option>
                                <option value="adjustment">
                                    {m.sl_new_reason_in_adjustment()}
                                </option>
                            </select>
                        </div>
                        <div>
                            <Label>{m.sl_new_notes()}</Label>
                            <Input
                                {...form.register('notes')}
                                placeholder={m.sl_new_notes_placeholder()}
                            />
                        </div>
                        {WITHDRAWAL_UI_ENABLED && reason === 'from_process' && (
                            <div className="md:col-span-2">
                                <Label className="mb-1 block">
                                    {m.scl_new_withdrawal_note_label()}
                                </Label>
                                <Combobox
                                    options={[
                                        {
                                            value: '',
                                            label: m.scl_new_withdrawal_note_none(),
                                        },
                                        ...withdrawalNoteOptions,
                                    ]}
                                    value={withdrawalNoteId}
                                    onChange={(v) => {
                                        setWithdrawalNoteId(v)
                                        // Đổi phiếu → xoá lô nguồn đã chọn để
                                        // buộc chọn lại theo phiếu mới (tránh
                                        // gửi lô của phiếu cũ → trừ trật).
                                        const items =
                                            form.getValues('items') ?? []
                                        items.forEach((_, i) => {
                                            form.setValue(
                                                `items.${i}.sources`,
                                                [],
                                            )
                                        })
                                    }}
                                    onSearchChange={setWithdrawalSearch}
                                    shouldFilter={false}
                                    selectedOption={
                                        withdrawalNoteId && pickedNoteData?.data
                                            ? {
                                                  value: pickedNoteData.data.id,
                                                  label: `${pickedNoteData.data.notes ?? pickedNoteData.data.code} · ${pickedNoteData.data.code}`,
                                              }
                                            : undefined
                                    }
                                    onOpenChange={(open) => {
                                        if (!open) setWithdrawalSearch('')
                                    }}
                                    onLoadMore={() =>
                                        void withdrawalNotesQuery.fetchNextPage()
                                    }
                                    hasMore={withdrawalNotesQuery.hasNextPage}
                                    placeholder={m.scl_new_withdrawal_note_placeholder()}
                                />
                                <p className="mt-1 text-sm text-muted-foreground">
                                    {m.scl_new_source_hint()}
                                </p>
                            </div>
                        )}
                    </div>

                    <LotItemsField
                        mode="in"
                        lotOptions={lotOptions}
                        categoryOptions={gradeOptions}
                        categoryLabel={m.sl_lot_grade()}
                        allowNewLot={reason !== 'return_from_packaging_process'}
                        renderExtraPerItem={
                            reason === 'from_process'
                                ? (idx) => (
                                      <LotSourcesSubTable
                                          idx={idx}
                                          options={sourceLotOptions}
                                          onSearchChange={setSourceSearch}
                                          clientFilter={usingNote}
                                          labels={{
                                              title: m.scl_new_sources_title(),
                                              empty: m.scl_new_sources_empty(),
                                              add: m.scl_new_source_add(),
                                              remove: m.scl_new_source_remove(),
                                              lotPlaceholder:
                                                  m.scl_new_source_lot_placeholder(),
                                              qtyLabel:
                                                  m.scl_new_source_qty_label(),
                                          }}
                                      />
                                  )
                                : undefined
                        }
                    />

                    {form.formState.errors.items?.message && (
                        <p className="text-sm text-destructive">
                            {form.formState.errors.items.message}
                        </p>
                    )}

                    <StickyFormFooter>
                        <Button
                            type="button"
                            variant="outline"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            onClick={onCancel}
                        >
                            {m.sl_new_cancel()}
                        </Button>
                        <Button
                            type="submit"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            disabled={isPending}
                        >
                            <Save className="size-4 mr-1" />
                            {submitLabel ??
                                (isPending
                                    ? m.sl_new_submitting()
                                    : m.sl_new_submit_pnk())}
                        </Button>
                    </StickyFormFooter>
                </form>
            </FormProvider>
        </div>
    )
}
